import { Link } from "react-router-dom";
import { ArrowRight, Droplet, Sunrise, Salad, GlassWater } from "lucide-react";
import PageBanner from "@/components/PageBanner";
import Reveal from "@/components/Reveal";
import { BENEFITS, STORY_IMG } from "@/data/site";

const RITUALS = [
  { icon: Sunrise, title: "Morning tonic", text: "1–2 tsp in warm water with a touch of honey to gently start your day." },
  { icon: Salad, title: "Bright dressings", text: "Whisk with olive oil, mustard and herbs for a vibrant vinaigrette." },
  { icon: GlassWater, title: "Afternoon refresh", text: "Add a splash to sparkling water with lemon for a crisp pick-me-up." },
  { icon: Droplet, title: "Before meals", text: "A diluted spoonful before eating to support comfortable digestion." },
];

export default function Benefits() {
  return (
    <div data-testid="benefits-page">
      <PageBanner
        eyebrow="Health & Benefits"
        title="The quiet power of raw apple cider vinegar"
        subtitle="For centuries, apple cider vinegar has been a trusted companion for everyday wellbeing. Here's how our raw, unfiltered blends can support your daily rhythm."
        testId="benefits-banner"
      />

      {/* Bento grid */}
      <section className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
          {/* big highlight */}
          <Reveal className="md:col-span-7">
            <div className="relative h-full overflow-hidden rounded-3xl border border-line">
              <img src={STORY_IMG} alt="Organic apples" className="h-72 w-full object-cover md:h-full" />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/70 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <h3 className="font-heading text-3xl text-cream md:text-4xl">Nature's original wellness tonic</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-cream/85">
                  Slow fermentation transforms simple apples into a living tonic rich in acetic acid,
                  enzymes and the beneficial "Mother".
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-5 md:col-span-5">
            {BENEFITS.slice(0, 2).map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div className="rounded-3xl border border-line bg-sand p-7">
                  <b.icon className="h-8 w-8 text-terracotta" strokeWidth={1.5} />
                  <h3 className="mt-4 font-heading text-2xl text-forest">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/65">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {BENEFITS.slice(2).map((b, i) => (
            <Reveal key={b.title} delay={i * 0.06} className="md:col-span-4">
              <div className="h-full rounded-3xl border border-line bg-cream p-7">
                <b.icon className="h-8 w-8 text-sage" strokeWidth={1.5} />
                <h3 className="mt-4 font-heading text-2xl text-forest">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink/65">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Rituals */}
      <section className="bg-sand py-16 md:py-24" data-testid="benefits-rituals">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage">How to enjoy it</p>
            <h2 className="mt-3 max-w-xl font-heading text-4xl font-medium leading-[1.05] tracking-tight text-forest md:text-5xl">
              Simple rituals for every part of the day
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {RITUALS.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.06}>
                <div className="h-full rounded-3xl border border-line bg-cream p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sand">
                    <r.icon className="h-6 w-6 text-sage" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-heading text-xl text-forest">{r.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/65">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-ink/50">
              Always dilute apple cider vinegar before drinking and avoid consuming it undiluted. If you
              are pregnant, nursing, or taking medication, consult your healthcare provider before use.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 text-center md:px-8 md:py-24">
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-heading text-3xl font-medium leading-[1.1] tracking-tight text-forest md:text-4xl">
            Ready to find your blend?
          </h2>
          <Link
            to="/products"
            data-testid="benefits-shop-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-sage px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-forest"
          >
            Browse the collection
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
